import React, { useState } from "react";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, MessageSquare } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally { setSending(false); }
  };

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <span className="font-data text-xs uppercase tracking-[0.2em] text-brand">◆ Get in touch</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black tracking-tight">Contact Us</h1>
          <p className="mt-3 text-slate-400">Questions about plans, the AI analyzer, or the academy? Send us a note and our team will reply by email.</p>
        </div>

        {sent ? (
          <div data-testid="contact-success-state" className="card-surface rounded-2xl p-10 text-center">
            <div className="h-16 w-16 rounded-full bg-gain/15 border border-gain/30 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-gain" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold">Thanks — message received</h2>
            <p className="text-slate-400 text-sm mt-2">We've emailed your enquiry to our team and will get back to you shortly.</p>
            <Button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "General enquiry", message: "" }); }} className="mt-6 bg-brand hover:bg-brand-hover text-white font-semibold">Send another</Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="card-surface rounded-2xl p-7 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Name</Label>
                <Input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="bg-[#0C1017] border-white/10 h-11 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="pl-9 bg-[#0C1017] border-white/10 h-11 text-white" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Subject</Label>
              <Input data-testid="contact-subject-input" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="bg-[#0C1017] border-white/10 h-11 text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Message</Label>
              <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" className="bg-[#0C1017] border-white/10 text-white min-h-[140px] resize-none" />
            </div>
            <Button data-testid="contact-submit-btn" type="submit" disabled={sending} className="w-full h-12 bg-brand hover:bg-brand-hover text-white font-semibold gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />} Send Message
            </Button>
            <p className="font-data text-[10px] text-slate-600 text-center">Your enquiry is emailed directly to the Netroy team. We never ask for passwords or card details.</p>
          </form>
        )}
      </section>
    </PageShell>
  );
}
